import { useMemo, useEffect, useRef } from 'react';
import { Box, CircularProgress, Typography } from '@locus-taxy/encore-mui';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';

import EntityGridView from './components/EntityGridView';
import CustomAppPage from './components/CustomAppPage';
import RouteWrapper from './components/RouteWrapper';
import {
  Routes,
  Route,
  Navigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { EntityDetails } from './components/EntityDetails';
import UserDetails from './components/UserDetails';
import {
  useClientId,
  hasSubdomainClientId,
  extractClientIdFromSubdomain,
} from './hooks/useClientId';
import { useAnalytics } from './hooks/useAnalytics';
import SettingsPage from './components/SettingsPage';
import AnalyticsPage from './components/AnalyticsPage';
import { OutboundContainer } from './OutboundContainer';
import { TourEditsContainer } from './customApps/TourEditsContainer';
import { MapDetailsPageContainer } from './customApps/MapDetailsPageContainer';
import { NetworkMapDetailsPageContainer } from './customApps/NetworkMapDetailsPageContainer';
import { RoutePath } from './types/categories';
import { buildClientPrefixedPath } from './utils/url';
import { NotFoundPage } from './components/NotFoundPage';
import AppTitleWithActions from './components/layout/AppTitleWithActions';
import DashboardSidebarFooter from './components/layout/DashboardSidebarFooter';
import { useAuth0 } from './auth';
import { createDashboardTheme } from './theme/dashboardTheme';
import useConfigureLocale from './hooks/useConfigureLocale';
import { EncoreRouterAppProvider } from './providers/EncoreRouterAppProvider';
import { useAppNavigation } from './hooks/useAppNavigation';
import { useClientMetadata } from './api';
import { useClientIdUrlCorrection } from './hooks/useClientIdUrlCorrection';

const SIDEBAR_EXPANDED_WIDTH = 240;

function EntityRouteWrapper() {
  const { entityName } = useParams();
  return (
    <RouteWrapper>
      <EntityGridView selectedEntity={entityName} />
    </RouteWrapper>
  );
}

function CustomAppRouteWrapper() {
  return (
    <RouteWrapper>
      <CustomAppPage />
    </RouteWrapper>
  );
}

function AppRegistryRouteWrapper() {
  const { appName } = useParams();
  return (
    <RouteWrapper>
      <EntityGridView selectedEntity={appName} />
    </RouteWrapper>
  );
}

function AnalyticsRouteWrapper() {
  return (
    <RouteWrapper>
      <AnalyticsPage />
    </RouteWrapper>
  );
}

function TourEditsRouteWrapper() {
  const [searchParams] = useSearchParams();
  const selectedTours = searchParams.get('selectedTours')?.split(',') || [];

  return (
    <RouteWrapper>
      <TourEditsContainer selectedTours={selectedTours} />
    </RouteWrapper>
  );
}

function MapDetailsRouteWrapper() {
  const { entityId } = useParams();
  return (
    <RouteWrapper>
      <MapDetailsPageContainer entityId={entityId} />
    </RouteWrapper>
  );
}

function NetworkMapDetailsRouteWrapper() {
  const { entityId } = useParams();
  return (
    <RouteWrapper>
      <NetworkMapDetailsPageContainer entityId={entityId} />
    </RouteWrapper>
  );
}

// Root redirect component with delay to allow Auth0 callback to complete
function RootRedirect({ toUrl }: { toUrl: string }) {
  return <Navigate to={toUrl} replace />;
}

// No dynamic module-based routes; using static RoutePath-based routes only

export default function App() {
  const clientId = useClientId();
  const { logout } = useAuth0();
  const hasTriggeredLogoutRef = useRef(false);

  const hostname = window.location.hostname;
  const hasSubdomain = hasSubdomainClientId(hostname);
  const subdomainClientId = extractClientIdFromSubdomain(hostname);

  // URL correction: For new URL structure, if route clientId differs from subdomain clientId, redirect
  useClientIdUrlCorrection();

  useEffect(() => {
    if (!clientId && !hasTriggeredLogoutRef.current) {
      hasTriggeredLogoutRef.current = true;
      try {
        logout({ logoutParams: { returnTo: window.location.origin } });
      } catch {
        // ignore logout errors
      }
    }
  }, [clientId, logout]);

  // Auto-fetch client metadata for new URL structure - block app if it fails
  const { isLoading: isMetadataLoading, isError: isMetadataError } =
    useClientMetadata(
      hasSubdomain && subdomainClientId ? subdomainClientId : null,
      {
        enabled: hasSubdomain && Boolean(subdomainClientId),
      }
    );

  const {
    navigation,
    navigationSignature,
    firstLeafPath,
    activeToolpadPath,
    isModulesLoading,
  } = useAppNavigation(clientId);

  useAnalytics({ clientId });

  // Configure locale
  useConfigureLocale();

  const branding = useMemo(
    () => ({
      homeUrl: buildClientPrefixedPath('/', clientId),
    }),
    [clientId]
  );

  // Create dashboard theme using extracted function
  const hybridTheme = useMemo(() => createDashboardTheme(), []);

  // For new URL structure, show loading or error state if metadata fetch is in progress or failed
  if (hasSubdomain && subdomainClientId) {
    if (isMetadataLoading) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (isMetadataError) {
      // Error toast is already shown by useClientMetadata hook
      // Block app rendering
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Typography variant="h6" color="error">
            Failed to load client metadata. Please refresh the page.
          </Typography>
        </Box>
      );
    }
  }

  return (
    <EncoreRouterAppProvider
      navigation={navigation}
      branding={branding}
      theme={hybridTheme}
      toolpadPath={activeToolpadPath}
    >
      <DashboardLayout
        key={navigationSignature}
        sidebarExpandedWidth={SIDEBAR_EXPANDED_WIDTH}
        slots={{
          appTitle: AppTitleWithActions,
          sidebarFooter: DashboardSidebarFooter,
        }}
        sx={{
          '& .MuiContainer-root': {
            p: 0,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          <Routes>
            <Route
              path={`/clientId/:clientId${RoutePath.ENTITIES}/:entityName`}
              element={<EntityRouteWrapper />}
            />
            <Route
              path={`/clientId/:clientId${RoutePath.ENTITIES}/:entityName/:entityId`}
              element={<EntityDetails />}
            />

            <Route
              path={`/clientId/:clientId/entities/:entityName/:entityId/outbound`}
              element={<OutboundContainer />}
            />
            <Route
              path={`/clientId/:clientId${RoutePath.APP_REGISTRY}/:appName`}
              element={<AppRegistryRouteWrapper />}
            />
            <Route
              path={`/clientId/:clientId${RoutePath.CUSTOM_APPS}/:appName`}
              element={<CustomAppRouteWrapper />}
            />
            <Route
              path={`/clientId/:clientId${RoutePath.TOUR_EDITS}`}
              element={<TourEditsRouteWrapper />}
            />
            <Route
              path={`/clientId/:clientId${RoutePath.MAP_DETAILS}/:entityId`}
              element={<MapDetailsRouteWrapper />}
            />
            <Route
              path={`/clientId/:clientId${RoutePath.NETWORK_MAP_DETAILS}/:entityId`}
              element={<NetworkMapDetailsRouteWrapper />}
            />
            <Route
              path={`/clientId/:clientId${RoutePath.SETTINGS}`}
              element={<SettingsPage />}
            />
            <Route
              path={`/clientId/:clientId${RoutePath.ACCOUNT}`}
              element={<UserDetails />}
            />
            <Route
              path={`/clientId/:clientId${RoutePath.ANALYTICS}/*`}
              element={<AnalyticsRouteWrapper />}
            />
            <Route
              path="/"
              element={
                <Navigate to={buildClientPrefixedPath('/', clientId)} replace />
              }
            />
            <Route
              path="clientId/:clientId"
              element={
                !isModulesLoading ? (
                  <RootRedirect
                    toUrl={buildClientPrefixedPath(
                      firstLeafPath || '/',
                      clientId
                    )}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100vh',
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )
              }
            />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Box>
      </DashboardLayout>
    </EncoreRouterAppProvider>
  );
}
